//deps
import React, { memo, ReactElement, useEffect, useMemo, useRef, useState } from "react";
import cx from "classnames";

//internal-deps
import { ConfirmOrderActionPayload } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/core-ui";
import { AUTOPAY_PAYMENT_FAILURE_SCREEN_TEST_IDS } from "@swiggy-private/payment-test-ids";

// constants
import { TEXTS, TIME_INTERVALS } from "@payment-client/components/autopay-payment-failure-screen/constants";

//styles
import styles from "./styles.scss";

//components
import { AutopayTickScreen } from "@payment-client/components/autopay-payment-failure-screen/components/tick-screen";
import { Shimmer } from "@payment-client/components/autopay-payment-failure-screen/components/shimmer";
import ExclamationSvgImage from "@payment-client/components/autopay-payment-failure-screen/components/exclamation/subtle-autoplace/Exclamation";

// hooks
import usePageVisibility from "@payment-client/hooks/usePageVisibility";

export interface AutopaySubtleConsentProps {
    onCancel: VoidFunction;
    payAsCash: VoidFunction;
    successPayload?: ConfirmOrderActionPayload;
    goToTrackScreen: VoidFunction;
    isSubtleConsentAPiCallDone: React.MutableRefObject<boolean>;
}

function AutopaySubtleConsent({
    onCancel,
    payAsCash,
    successPayload,
    goToTrackScreen,
    isSubtleConsentAPiCallDone,
}: AutopaySubtleConsentProps): ReactElement {
    const [showShimmer, setShowShimmer] = useState(false);
    const [showMainContent, setShowMainContent] = useState(false);
    const [scaleUp, setScaleUp] = useState(false);
    const isPageVisibleToUser = usePageVisibility();

    const timeout = useRef<NodeJS.Timeout | null>(null);
    const mainContentTimeout = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        if (!isPageVisibleToUser || mainContentTimeout.current) {
            return;
        }

        mainContentTimeout.current = setTimeout(() => {
            setShowMainContent(true);
            setScaleUp(true);
        }, TIME_INTERVALS.SUBTLE_AUTOPLACE.SHOW_MAIN_CONTENT);
    }, [isPageVisibleToUser]);

    useEffect(() => {
        if (!isPageVisibleToUser) {
            return;
        }

        if (isSubtleConsentAPiCallDone.current || timeout.current) {
            return;
        }

        timeout.current = setTimeout(() => {
            isSubtleConsentAPiCallDone.current = true;
            setShowShimmer(true);
            payAsCash();
        }, TIME_INTERVALS.SUBTLE_AUTOPLACE.API_CALL);
    }, [isSubtleConsentAPiCallDone, isPageVisibleToUser, payAsCash]);

    useEffect(() => {
        // only need to clear timeout when component unmounts
        return () => {
            timeout.current && clearTimeout(timeout.current);
            mainContentTimeout.current && clearTimeout(mainContentTimeout.current);
        };
    }, []);

    const exclamationContainerClassname = useMemo(() => {
        return cx(styles.exclamationContainerFullpage, {
            [styles.exclamationContainerFullpageAnimate]: scaleUp,
        });
    }, [scaleUp]);

    const mainContainerClassname = useMemo(() => {
        return cx(styles.mainContainer, {
            [styles.mainContainerAnimate]: showMainContent,
        });
    }, [showMainContent]);

    if (!isEmpty(successPayload)) {
        return <AutopayTickScreen goToTrackScreen={goToTrackScreen} isSubtleAutoplaceFlow />;
    }

    if (showShimmer) {
        return <Shimmer />;
    }
    return (
        <div className={styles.wrapper}>
            <div className={exclamationContainerClassname}>
                <ExclamationSvgImage className={styles.exclamationSvg} />
                <h3
                    className={styles.exclamationTitle}
                    data-testid={AUTOPAY_PAYMENT_FAILURE_SCREEN_TEST_IDS.ERROR_TITLE}
                    tabIndex={0}
                >
                    {TEXTS.ERROR_TITLE}
                </h3>
            </div>
            {showMainContent && (
                <>
                    <h2
                        className={mainContainerClassname}
                        data-testid={AUTOPAY_PAYMENT_FAILURE_SCREEN_TEST_IDS.ERROR_SUB_TITLE}
                        tabIndex={0}
                    >
                        {TEXTS.SUBTLE_CONSENT_TITLE}
                    </h2>

                    <button
                        className={styles.cancel}
                        onClick={onCancel}
                        data-testid={AUTOPAY_PAYMENT_FAILURE_SCREEN_TEST_IDS.CANCEL}
                    >
                        {TEXTS.CANCEL}
                    </button>

                    <div
                        className={styles.sliderContainer}
                        data-testid={AUTOPAY_PAYMENT_FAILURE_SCREEN_TEST_IDS.LOADING_INDICATOR}
                    >
                        <div className={styles.slider}>
                            <div className={styles.sliderIndicator}></div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default memo(AutopaySubtleConsent);
